import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Warning, ArrowClockwise, Check, X as XIcon } from "@phosphor-icons/react";

/**
 * Admin Rescue tab — lists IAP transactions where the customer WAS
 * charged by Apple/Google but our backend couldn't verify the receipt
 * (401 / 404 / etc.). Admin can one-click Grant Pro (fulfills the plan)
 * or Reject (marks as fraudulent / duplicate).
 *
 * Fed by GET  /api/iap/admin/pending-verify
 *          POST /api/iap/admin/pending-verify/:tx_id/grant
 *          POST /api/iap/admin/pending-verify/:tx_id/reject
 */
export default function PendingVerifyTab() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/iap/admin/pending-verify", { params: { limit: 100 } });
            setRows(data.pending || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load pending IAP");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    async function onGrant(tx) {
        if (!confirm(`Grant ${tx.plan} to ${tx.user_email}?\n\nThis will upgrade the user immediately — only do this if you've confirmed the payment in your Stripe/Apple/Google dashboard.`)) return;
        setBusyId(tx.id);
        try {
            await api.post(`/iap/admin/pending-verify/${tx.id}/grant`);
            toast.success(`Granted ${tx.plan} to ${tx.user_email}`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Grant failed");
        } finally {
            setBusyId(null);
        }
    }

    async function onReject(tx) {
        if (!confirm(`Reject this ${tx.plan} attempt for ${tx.user_email}?\n\nUse this if the user later retried and succeeded, OR if the payment turned out to be fraud.`)) return;
        setBusyId(tx.id);
        try {
            await api.post(`/iap/admin/pending-verify/${tx.id}/reject`);
            toast.success("Marked as rejected");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reject failed");
        } finally {
            setBusyId(null);
        }
    }

    return (
        <div data-testid="pending-verify-tab">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-2xl font-bold flex items-center gap-2">
                        <Warning size={22} className="text-amber-500" weight="fill" />
                        Failed IAP Verifications
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        Customers whose card was charged by Apple/Google but whose receipt failed server-side verification.
                        Use this to rescue affected users manually.
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="pending-verify-refresh">
                    <ArrowClockwise size={14} className="mr-2" />
                    Refresh
                </Button>
            </div>

            {loading ? (
                <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
            ) : rows.length === 0 ? (
                <div className="text-sm text-muted-foreground py-12 text-center rounded-2xl border border-border bg-card">
                    ✅ No failed IAP verifications. Everything's clean.
                </div>
            ) : (
                <div className="space-y-3">
                    {rows.map((tx) => (
                        <div
                            key={tx.id}
                            className="rounded-2xl border border-border bg-card p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
                            data-testid={`pending-row-${tx.id}`}
                        >
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold text-sm truncate">{tx.user_email || tx.user_id}</span>
                                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#6C63FF]/10 text-[#6C63FF] font-bold">
                                        {tx.iap_platform}
                                    </span>
                                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 font-bold">
                                        {tx.plan}
                                    </span>
                                </div>
                                <div className="text-xs text-muted-foreground mt-1 truncate">
                                    Product: <code className="font-mono">{tx.iap_product_id}</code> · Ext-ID: <code className="font-mono">{(tx.iap_external_id || "").slice(0, 32)}…</code>
                                </div>
                                <div className="text-xs text-red-600 mt-1 break-words">
                                    ⚠️ {tx.verify_error || "Unknown verification error"}
                                </div>
                                <div className="text-[11px] text-muted-foreground mt-1">
                                    {new Date(tx.created_at).toLocaleString()}
                                </div>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                                <Button
                                    size="sm"
                                    onClick={() => onGrant(tx)}
                                    disabled={busyId === tx.id}
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                    data-testid={`grant-${tx.id}`}
                                >
                                    <Check size={14} className="mr-1" /> Grant
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => onReject(tx)}
                                    disabled={busyId === tx.id}
                                    data-testid={`reject-${tx.id}`}
                                >
                                    <XIcon size={14} className="mr-1" /> Reject
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
